import type { Serializer } from "@vueuse/core";
import { cloneDeep } from "lodash-es";

export const onboardingEntrySerializer: Serializer<
  OnboardingEntry | undefined
> = {
  read: (raw) => {
    if (!raw) return undefined;

    const parsedEntry = JSON.parse(raw);

    // check if the entry is in the OnboardingEntryType enum
    const isValidEntry =
      parsedEntry?.type &&
      Object.values(OnboardingEntryType).includes(parsedEntry.type);

    if (isValidEntry) {
      return parsedEntry;
    }

    return undefined;
  },
  write: (value) => {
    if (!value) return "";

    return JSON.stringify(value);
  },
};

const useOnboardingStore = () => {
  const _persistedOnboardingEntry = useStorageSafely<
    OnboardingEntry | undefined
  >({
    type: "localStorage",
    key: ONBOARDING_ENTRY_STORAGE_NAME,
    defaultValue: undefined,
    options: {
      deep: true,
      serializer: onboardingEntrySerializer,
      flush: "pre",
    },
  });

  // Get state from localStorage and set it to in-memory ref
  const onboardingEntry = useState<OnboardingEntry | undefined>(
    ONBOARDING_ENTRY_STORAGE_NAME,
    () =>
      _persistedOnboardingEntry.value
        ? cloneDeep(_persistedOnboardingEntry.value)
        : undefined,
  );

  // Clean up localStorage state
  _persistedOnboardingEntry.value = undefined;

  // If we update the in-memory ref, we want to update the localStorage state
  watch(
    onboardingEntry,
    (newState) => {
      _persistedOnboardingEntry.value = newState;
    },
    {
      deep: true,
    },
  );

  return onboardingEntry;
};

export default useOnboardingStore;
